import { Action, getModule, Module, Mutation, VuexModule } from 'vuex-module-decorators';
import { getQuestionnaireResponse, submitQuestionResponse } from '@api/questionnaire';
import { getSignupCheckoutSelections, updateQuickInvest } from '@api/checkout';
import { QuestionnaireQuestion, QuestionnaireRecommendation, QuestionnaireResponse } from 'types/questionnaire';
import { EmailCaptureFields } from 'types/layout/lead';
import { getDistinctId } from '@utils/analytics';
import { orderForm } from '@store/modules/order-form';
import { serviceLevelWithPlansByName } from '@utils/service-levels';
import { SignupCheckoutSelectionResponse } from 'types/checkout';
import store from '..';

@Module({
	dynamic: true,
	namespaced: true,
	name: 'questionnaire',
	store
})
class QuestionnaireModule extends VuexModule {
	email: string | undefined;
	emailCaptureFields: EmailCaptureFields = { existingUser: false, mixpanelId: '', newLead: false };
	fullResponse: QuestionnaireResponse = { questions: [] };
	dataRetrieved = false;
	signupCheckoutResponse: SignupCheckoutSelectionResponse = { quickInvest: false, giftedAccount: false };

	get distinctId(): string | undefined {
		if (this.emailCaptureFields.mixpanelId) {
			return this.emailCaptureFields.mixpanelId;
		}

		return getDistinctId();
	}

	get recommendation(): QuestionnaireRecommendation {
		const result: QuestionnaireRecommendation = {};
		if (this.fullResponse.recommendedServiceLevel) {
			result.serviceLevel = this.fullResponse.recommendedServiceLevel;
		}

		if (
			this.fullResponse.recommendedRiaPlan &&
			serviceLevelWithPlansByName(this.fullResponse.recommendedServiceLevel)
		) {
			result.riaPlan = this.fullResponse.recommendedRiaPlan;
		}
		return result;
	}

	get isGiftedAccount(): boolean {
		return this.signupCheckoutResponse.giftedAccount;
	}

	get isQuickInvest(): boolean {
		return this.signupCheckoutResponse.quickInvest;
	}

	@Action
	public async getFullResponse(ignore404?: boolean): Promise<void> {
		if (this.distinctId) {
			const [fullResponse, signupCheckoutResponse] = await Promise.all([
				getQuestionnaireResponse(!!ignore404),
				getSignupCheckoutSelections()
			]);

			this.UPDATE_DATA_RETRIEVED(true);
			this.UPDATE_FULL_RESPONSE(fullResponse);
			this.UPDATE_SIGNUP_CHECKOUT_RESPONSE(signupCheckoutResponse);
		}
	}

	@Action
	public async submitQuestionResponse(question: QuestionnaireQuestion): Promise<void> {
		if (this.distinctId) {
			const resultQuestion = await submitQuestionResponse(question.id, question.questionResponse);
			this.UPDATE_QUESTION_RESPONSE(resultQuestion);
		}
	}

	@Action
	public async updateQuickInvest(quickInvest: boolean): Promise<void> {
		const response = await updateQuickInvest(quickInvest);
		this.UPDATE_SIGNUP_CHECKOUT_RESPONSE(response);

		if (response.selections) {
			orderForm.UPDATE_SERVICE_LEVEL(response.selections.accountLevel);
			orderForm.UPDATE_PLAN(response.selections.plan);
		}
	}

	@Mutation
	public UPDATE_FULL_RESPONSE(state: QuestionnaireResponse): void {
		if (this.fullResponse.questions.length > 0) {
			state.questions.forEach((question) => {
				const oldQuestion = this.fullResponse.questions.find((old) => old.id == question.id);
				if (oldQuestion) {
					question.questionResponse = oldQuestion.questionResponse;
				}
			});
		}
		this.fullResponse = state;
	}

	@Mutation
	public UPDATE_EMAIL(email: string): void {
		this.email = email;
	}

	@Mutation
	public UPDATE_EMAIL_CAPTURE_FIELDS(newFields: EmailCaptureFields): void {
		this.emailCaptureFields = newFields;
	}

	@Mutation
	public UPDATE_QUESTION_RESPONSE(question: QuestionnaireQuestion): void {
		const questionToUpdate = this.fullResponse.questions.find((q) => q.id === question.id);
		if (questionToUpdate) {
			questionToUpdate.questionResponse = question.questionResponse;
			questionToUpdate.nextQuestion = question.nextQuestion;
		}
	}

	@Mutation
	public UPDATE_SIGNUP_CHECKOUT_RESPONSE(signupCheckoutResponse: SignupCheckoutSelectionResponse): void {
		this.signupCheckoutResponse = signupCheckoutResponse;
	}

	@Mutation
	public UPDATE_DATA_RETRIEVED(newValue: boolean): void {
		this.dataRetrieved = newValue;
	}
}

export const questionnaire = getModule(QuestionnaireModule);
